/**
 * ConnectorUpdate Component
 * ==========================
 * Shows connector version status and allows manual download updates.
 * Displays in the Operator Settings tab.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  ArrowCircleUp,
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  DownloadSimple,
  Terminal,
  WarningCircle
} from '@phosphor-icons/react';
export const ConnectorUpdate = ({ agentId, connectorKeys = [], connectorStatus = null }) => {
  const [latestVersion, setLatestVersion] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  
  // Get the most recently used connector's version
  const activeConnector = connectorKeys.find(k => k.last_used_at) || connectorKeys[0];
  const currentVersion = activeConnector?.connector_version || 'Unknown';
  const lastUsed = activeConnector?.last_used_at;
  
  // Check if update is needed
  const parseVersion = (v) => {
    if (!v || v === 'Unknown') return [0, 0, 0];
    const parts = v.replace('v', '').split('.');
    return parts.map(p => parseInt(p) || 0);
  };
  
  const isUpdateAvailable = () => {
    if (!latestVersion || currentVersion === 'Unknown') return false;
    const current = parseVersion(currentVersion);
    const latest = parseVersion(latestVersion);
    
    for (let i = 0; i < 3; i++) {
      if (latest[i] > current[i]) return true;
      if (latest[i] < current[i]) return false;
    }
    return false;
  };
  
  // Fetch latest version on mount
  useEffect(() => {
    checkForUpdates(false);
  }, []);
  
  const checkForUpdates = async (showToast = true) => {
    setChecking(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/connector/version`);
      
      if (response.ok) {
        const data = await response.json();
        setLatestVersion(data.version);
        setChangelog(data.changelog || []);
        
        if (showToast) {
          if (isUpdateAvailable()) {
            toast.success(`Update available: v${data.version}`);
          } else {
            toast.success('Connector is up to date!');
          }
        }
      } else {
        setError('Failed to check for updates');
        if (showToast) toast.error('Failed to check for updates');
      }
    } catch (err) {
      console.error('[CONNECTOR_UPDATE] Check failed:', err);
      setError('Network error');
      if (showToast) toast.error('Failed to check for updates');
    } finally {
      setChecking(false);
    }
  };
  
  // DownloadSimple connector
  const downloadConnector = () => {
    window.open(`${BACKEND_URL}/api/exec/connector/connector.py`, '_blank');
    toast.success('Download started!', {
      description: 'Replace your connector_v3.py file and restart the connector.',
      duration: 8000
    });
  };
  
  const updateAvailable = isUpdateAvailable();
  
  return (
    <div className="glass rounded-xl p-5" data-testid="connector-update-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${updateAvailable ? 'bg-amber-500/20' : 'bg-green-500/20'}`}>
          {updateAvailable ? (
            <ArrowCircleUp size={20} className="text-amber-400" />
          ) : (
            <CheckCircle size={20} className="text-[#00E5C0]" />
          )}
        </div>
        <div>
          <h3 className="text-white font-medium">Connector Updates</h3>
          <p className="text-[#A0AEC0] text-sm">Keep your connector up to date</p>
        </div>
      </div>
      
      {/* Version Info */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
          <div className="flex items-center gap-2">
            <Terminal size={16} className="text-[#A0AEC0]" />
            <span className="text-sm text-[#A0AEC0]">Installed Version</span>
          </div>
          <span className={`text-sm font-mono ${currentVersion === 'Unknown' ? 'text-[#7D8896]' : 'text-white'}`}>
            {currentVersion === 'Unknown' ? 'Not detected' : `v${currentVersion}`}
          </span>
        </div>
        
        <div className="flex items-center justify-between p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
          <div className="flex items-center gap-2">
            <ArrowCircleUp size={16} className="text-[#A0AEC0]" />
            <span className="text-sm text-[#A0AEC0]">Latest Version</span>
          </div>
          {checking ? (
            <CircleNotch size={16} className="animate-spin text-[#A0AEC0]" />
          ) : latestVersion ? (
            <span className="text-sm font-mono text-white">v{latestVersion}</span>
          ) : (
            <span className="text-sm text-[#7D8896]">--</span>
          )}
        </div>
      </div>
      
      {/* Update Status Badge */}
      {latestVersion && (
        <div className={`mb-4 p-3 rounded-lg ${
          updateAvailable 
            ? 'bg-amber-500/10 border border-amber-500/30' 
            : 'bg-green-500/10 border border-green-500/30'
        }`}>
          {updateAvailable ? (
            <div className="flex items-start gap-2">
              <WarningCircle size={16} className="text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-400 text-sm font-medium">Update Available</p>
                <p className="text-amber-400/70 text-xs mt-1">
                  A new version is available. DownloadSimple and install to get the latest features and fixes.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-[#00E5C0]" />
              <p className="text-[#00E5C0] text-sm">Your connector is up to date!</p>
            </div>
          )}
        </div>
      )}
      
      {/* Changelog */}
      {updateAvailable && changelog.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-[rgba(255,255,255,0.02)]">
          <p className="text-xs text-[#A0AEC0] mb-2">What&apos;s new in v{latestVersion}:</p>
          <ul className="space-y-1">
            {changelog.slice(0, 4).map((change, idx) => (
              <li key={idx} className="text-xs text-[#A0AEC0]/80 flex items-start gap-1.5">
                <span className="text-[#00E5C0]">•</span>
                {change}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => checkForUpdates(true)}
          disabled={checking}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all disabled:opacity-50"
          data-testid="check-updates-btn"
        >
          {checking ? (
            <CircleNotch size={16} className="animate-spin" />
          ) : (
            <ArrowsClockwise size={16} />
          )}
          Check for Updates
        </button>
        
        {updateAvailable && (
          <button
            onClick={downloadConnector}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0]/20 text-[#00E5C0] hover:bg-[#00E5C0]/30 transition-all"
            data-testid="download-update-btn"
          >
            <DownloadSimple size={16} />
            DownloadSimple Update
          </button>
        )}
      </div>
      
      {/* Manual update instructions */}
      {updateAvailable && (
        <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <p className="text-xs text-amber-400 font-medium mb-2">📋 Update Steps:</p>
          <ol className="text-xs text-amber-400/80 space-y-1 list-decimal list-inside">
            <li>Click &quot;DownloadSimple Update&quot; above</li>
            <li>Replace your existing <code className="bg-[#11161D] px-1 rounded">connector_v3.py</code> file</li>
            <li>Restart connector: <code className="bg-[#11161D] px-1 rounded text-[#00E5C0]">python connector_v3.py --key YOUR_KEY</code></li>
          </ol>
        </div>
      )}
      
      {/* Help Text */}
      <div className="mt-4 pt-3 border-t border-white/10">
        <p className="text-xs text-[#A0AEC0]/60">
          💡 Tip: DownloadSimple the latest connector and replace your local file to update.
        </p>
      </div>
      
      {/* Connector Status Indicator */}
      {connectorStatus && (
        <div className={`mt-2 flex items-center gap-2 text-xs ${connectorStatus.is_polling ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`}>
          <div className={`w-2 h-2 rounded-full ${connectorStatus.is_polling ? 'bg-green-400 animate-pulse' : 'bg-zinc-500'}`} />
          {connectorStatus.is_polling ? 'Connector online' : connectorStatus.message || 'Connector offline'}
        </div>
      )}
      
      {/* Last Seen - only show if no real-time status */}
      {!connectorStatus?.is_polling && lastUsed && (
        <p className="text-xs text-[#A0AEC0]/40 mt-2">
          Last connector activity: {new Date(lastUsed).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default ConnectorUpdate;
